import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { Editor, Transforms, createEditor, Descendant, BaseEditor, Element as SlateElement } from 'slate';
import { Slate, Editable, withReact, ReactEditor } from 'slate-react';
import { withHistory } from 'slate-history';
import { Button } from './Button';

// Define custom types for Slate
type CustomElement = {
  type: 'paragraph' | 'heading-one' | 'heading-two' | 'heading-three' | 'block-quote' | 'list-item' | 'bulleted-list' | 'numbered-list' | 'link';
  children: CustomText[];
  url?: string;
}

type CustomText = {
  text: string;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  code?: boolean;
}

declare module 'slate' {
  interface CustomTypes {
    Editor: BaseEditor & ReactEditor;
    Element: CustomElement;
    Text: CustomText;
  }
}

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const initialValue: Descendant[] = [
  {
    type: 'paragraph',
    children: [{ text: '' }],
  },
];

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Write your content here...',
  className = '',
  disabled = false
}) => {
  const [editor] = useState(() => withHistory(withReact(createEditor())));
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle editor keyboard shortcuts
  // Note: Previous implementation blocked K, J, R letters to prevent browser extension conflicts,
  // but this prevented normal typing. Browser extension conflicts should be handled by users
  // disabling conflicting extensions, not by breaking core functionality.
  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    // Handle only editor shortcuts with modifier keys
    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case 'b':
          event.preventDefault();
          toggleMark(editor, 'bold');
          return;
        case 'i':
          event.preventDefault();
          toggleMark(editor, 'italic');
          return;
        case 'u':
          event.preventDefault();
          toggleMark(editor, 'underline');
          return;
      }
    }
  }, [editor]);
  
  // Parse the value from string to Slate format
  const slateValue = useMemo(() => {
    if (!value) return initialValue;
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : initialValue;
    } catch {
      // If it's plain text, convert to Slate format
      return [
        {
          type: 'paragraph' as const,
          children: [{ text: value }],
        },
      ];
    }
  }, [value]);

  // Handle content changes
  const handleChange = useCallback((newValue: Descendant[]) => {
    // Convert Slate value to string for external handling
    const isAstChange = editor.operations.some(
      op => 'set_selection' !== op.type
    );
    if (isAstChange) {
      // For now, we'll serialize as JSON, but could also convert to HTML
      onChange(JSON.stringify(newValue));
    }
  }, [editor, onChange]);

  // Define our schema's rendering behavior.
  const renderElement = useCallback((props: any) => {
    switch (props.element.type) {
      case 'heading-one':
        return <h1 className="text-2xl font-bold my-2 text-gray-900" {...props.attributes}>{props.children}</h1>;
      case 'heading-two':
        return <h2 className="text-xl font-semibold my-2 text-gray-900" {...props.attributes}>{props.children}</h2>;
      case 'heading-three':
        return <h3 className="text-lg font-medium my-1 text-gray-900" {...props.attributes}>{props.children}</h3>;
      case 'block-quote':
        return <blockquote className="border-l-4 border-primary-500 pl-4 py-1 my-2 italic text-gray-700 bg-gray-50 rounded-r" {...props.attributes}>{props.children}</blockquote>;
      case 'bulleted-list':
        return <ul className="list-disc list-inside my-2 space-y-1" {...props.attributes}>{props.children}</ul>;
      case 'numbered-list':
        return <ol className="list-decimal list-inside my-2 space-y-1" {...props.attributes}>{props.children}</ol>;
      case 'list-item':
        return <li className="my-0.5" {...props.attributes}>{props.children}</li>;
      case 'link':
        return (
          <a
            {...props.attributes}
            href={props.element.url}
            className="text-primary-600 underline hover:text-primary-800"
            target="_blank"
            rel="noopener noreferrer"
          >
            {props.children}
          </a>
        );
      default:
        return <p className="my-1" {...props.attributes}>{props.children}</p>;
    }
  }, []);

  // Define a rendering function for leaves (text formatting)
  const renderLeaf = useCallback((props: any) => {
    let { attributes, children, leaf } = props;

    if (leaf.bold) {
      children = <strong>{children}</strong>;
    }

    if (leaf.italic) {
      children = <em>{children}</em>;
    }

    if (leaf.underline) {
      children = <u>{children}</u>;
    }

    if (leaf.code) {
      children = <code className="bg-gray-100 px-1 py-0.5 rounded text-sm font-mono text-pink-600">{children}</code>;
    }

    return <span {...attributes}>{children}</span>;
  }, []);

  // Helper functions for formatting
  const isMarkActive = (editor: Editor, format: string) => {
    const marks = Editor.marks(editor);
    return marks ? (marks as any)[format] === true : false;
  };

  const toggleMark = (editor: Editor, format: string) => {
    const isActive = isMarkActive(editor, format);

    if (isActive) {
      Editor.removeMark(editor, format);
    } else {
      Editor.addMark(editor, format, true);
    }
  };

  const isBlockActive = (editor: Editor, format: string) => {
    const { selection } = editor;
    if (!selection) return false;

    const [match] = Array.from(
      Editor.nodes(editor, {
        at: Editor.unhangRange(editor, selection),
        match: n =>
          !Editor.isEditor(n) && SlateElement.isElement(n) && (n as any).type === format,
      })
    );

    return !!match;
  };

  const toggleBlock = (editor: Editor, format: string) => {
    const isActive = isBlockActive(editor, format);
    const isList = ['numbered-list', 'bulleted-list'].includes(format);

    Transforms.unwrapNodes(editor, {
      match: n =>
        !Editor.isEditor(n) &&
        SlateElement.isElement(n) &&
        ['numbered-list', 'bulleted-list'].includes((n as any).type),
      split: true,
    });

    const newProperties: Partial<CustomElement> = {
      type: isActive ? 'paragraph' : isList ? 'list-item' : (format as any),
    };
    Transforms.setNodes<SlateElement>(editor, newProperties);

    if (!isActive && isList) {
      const block = { type: format as any, children: [] };
      Transforms.wrapNodes(editor, block);
    }
  };

  // Toolbar button component
  const ToolbarButton: React.FC<{
    active: boolean;
    onMouseDown: (event: React.MouseEvent) => void;
    children: React.ReactNode;
    title: string;
  }> = ({ active, onMouseDown, children, title }) => (
    <button
      type="button"
      title={title}
      className={`px-2 py-1 rounded text-sm font-medium transition-colors ${
        active
          ? 'bg-blue-100 text-blue-700'
          : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
      }`}
      onMouseDown={onMouseDown}
    >
      {children}
    </button>
  );

  return (
    <div
      ref={containerRef}
      className={`border border-gray-300 rounded-md ${className}`}
    >
      {!disabled && (
        <div className="border-b border-gray-200 p-2 bg-gray-50">
          <div className="flex flex-wrap gap-1">
            {/* Text formatting */}
            <ToolbarButton
              active={isMarkActive(editor, 'bold')}
              onMouseDown={(event) => {
                event.preventDefault();
                toggleMark(editor, 'bold');
              }}
              title="Bold"
            >
              <strong>B</strong>
            </ToolbarButton>
            
            <ToolbarButton
              active={isMarkActive(editor, 'italic')}
              onMouseDown={(event) => {
                event.preventDefault();
                toggleMark(editor, 'italic');
              }}
              title="Italic"
            >
              <em>I</em>
            </ToolbarButton>
            
            <ToolbarButton
              active={isMarkActive(editor, 'underline')}
              onMouseDown={(event) => {
                event.preventDefault();
                toggleMark(editor, 'underline');
              }}
              title="Underline"
            >
              <u>U</u>
            </ToolbarButton>
            
            <ToolbarButton
              active={isMarkActive(editor, 'code')}
              onMouseDown={(event) => {
                event.preventDefault();
                toggleMark(editor, 'code');
              }}
              title="Code"
            >
              <code>&lt;/&gt;</code>
            </ToolbarButton>

            <div className="border-l border-gray-300 mx-1"></div>

            {/* Block formatting */}
            <ToolbarButton
              active={isBlockActive(editor, 'heading-one')}
              onMouseDown={(event) => {
                event.preventDefault();
                toggleBlock(editor, 'heading-one');
              }}
              title="Heading 1"
            >
              H1
            </ToolbarButton>
            
            <ToolbarButton
              active={isBlockActive(editor, 'heading-two')}
              onMouseDown={(event) => {
                event.preventDefault();
                toggleBlock(editor, 'heading-two');
              }}
              title="Heading 2"
            >
              H2
            </ToolbarButton>
            
            <ToolbarButton
              active={isBlockActive(editor, 'heading-three')}
              onMouseDown={(event) => {
                event.preventDefault();
                toggleBlock(editor, 'heading-three');
              }}
              title="Heading 3"
            >
              H3
            </ToolbarButton>

            <div className="border-l border-gray-300 mx-1"></div>

            <ToolbarButton
              active={isBlockActive(editor, 'block-quote')}
              onMouseDown={(event) => {
                event.preventDefault();
                toggleBlock(editor, 'block-quote');
              }}
              title="Block Quote"
            >
              "
            </ToolbarButton>
            
            <ToolbarButton
              active={isBlockActive(editor, 'bulleted-list')}
              onMouseDown={(event) => {
                event.preventDefault();
                toggleBlock(editor, 'bulleted-list');
              }}
              title="Bulleted List"
            >
              •
            </ToolbarButton>
            
            <ToolbarButton
              active={isBlockActive(editor, 'numbered-list')}
              onMouseDown={(event) => {
                event.preventDefault();
                toggleBlock(editor, 'numbered-list');
              }}
              title="Numbered List"
            >
              1.
            </ToolbarButton>
          </div>
        </div>
      )}
      
      <div className="p-4">
        <Slate
          editor={editor}
          initialValue={slateValue}
          onChange={handleChange}
        >
          <Editable
            className={`min-h-32 focus:outline-none ${disabled ? 'bg-gray-50 cursor-not-allowed' : ''}`}
            renderElement={renderElement}
            renderLeaf={renderLeaf}
            placeholder={placeholder}
            readOnly={disabled}
            onKeyDown={handleKeyDown}
            onKeyDownCapture={handleKeyDown}
            style={{ outline: 'none' }}
            spellCheck={false}
            data-slate-editor="true"
            role="textbox"
          />
        </Slate>
      </div>
    </div>
  );
};

export default RichTextEditor;